import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showModal, hideModal, installModalFallback } from "./modalHelper";
import {
    API, CATEGORIES_DEPENSE, findCategorieDepense, unwrap, apiMessage, toast,
    formatAmount, formatDate, toDateInput,
} from "./projetsCommon";

const DEPENSE_MODAL = "depenseModal";
const today = () => new Date().toISOString().substring(0, 10);
const emptyDepense = () => ({ date: today(), categorie: 1, libelle: "", montant: "", supplierId: "", reference: "" });

const schema = Yup.object({
    date: Yup.string().required("La date est obligatoire"),
    libelle: Yup.string().trim().required("Le libellé est obligatoire").max(200, "200 caractères maximum"),
    montant: Yup.number().typeError("Montant invalide").required("Le montant est obligatoire").moreThan(0, "Le montant doit être > 0"),
});

const Kpi = ({ label, value, sub, tone }) => (
    <div className="col-6 col-lg-3 mb-3">
        <div className={`gp-fin-kpi ${tone ? `gp-fin-${tone}` : ""}`}>
            <div className="gp-info-label">{label}</div>
            <div className="gp-fin-value">{value}</div>
            {sub && <div className="small text-muted">{sub}</div>}
        </div>
    </div>
);

/** Onglet « Budget & dépenses » de la fiche projet. */
const ProjetFinances = ({ projet }) => {
    const [fin, setFin] = useState(null);
    const [depenses, setDepenses] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [editing, setEditing] = useState(null);

    useEffect(() => installModalFallback(), []);
    useEffect(() => {
        load();
        axiosInstance.get(API.suppliers)
            .then((res) => setSuppliers((unwrap(res) || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
            .catch(() => setSuppliers([]));
    }, [projet.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = () => {
        axiosInstance.get(API.finances(projet.id))
            .then((res) => setFin(unwrap(res)))
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les finances")));
        axiosInstance.get(API.depenses(projet.id))
            .then((res) => setDepenses(unwrap(res) || []))
            .catch(() => setDepenses([]));
    };

    const formik = useFormik({
        initialValues: emptyDepense(),
        validationSchema: schema,
        onSubmit: async (v, { setErrors }) => {
            const payload = {
                date: v.date,
                categorie: Number(v.categorie),
                libelle: v.libelle.trim(),
                montant: Number(v.montant),
                supplierId: v.supplierId ? Number(v.supplierId) : null,
                reference: v.reference?.trim() || null,
            };
            try {
                if (editing) await axiosInstance.put(API.depense(projet.id, editing.id), payload);
                else await axiosInstance.post(API.depenses(projet.id), payload);
                hideModal(DEPENSE_MODAL);
                toast("success", editing ? "Dépense modifiée" : "Dépense ajoutée");
                load();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openCreate = () => { setEditing(null); formik.resetForm({ values: emptyDepense() }); showModal(DEPENSE_MODAL); };
    const openEdit = (d) => {
        setEditing(d);
        formik.resetForm({
            values: {
                date: toDateInput(d.date), categorie: findCategorieDepense(d.categorie).value, libelle: d.libelle,
                montant: d.montant, supplierId: d.supplierId ?? "", reference: d.reference ?? "",
            },
        });
        showModal(DEPENSE_MODAL);
    };
    const remove = (d) => {
        Swal.fire({
            title: `Supprimer la dépense « ${d.libelle} » ?`, icon: "warning",
            showCancelButton: true, confirmButtonText: "Supprimer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try {
                await axiosInstance.delete(API.depense(projet.id, d.id));
                toast("success", "Dépense supprimée");
                load();
            } catch (error) { toast("error", apiMessage(error, "Suppression échouée")); }
        });
    };

    if (!fin) return <p>Chargement...</p>;

    const conso = fin.consommationBudget;
    const consoTone = conso == null ? "" : conso > 100 ? "bg-danger" : conso > 85 ? "bg-warning" : "bg-success";
    const totalCharges = fin.parCategorie.reduce((s, c) => s + c.montant, 0) || 1;
    const invalid = (n) => (formik.touched[n] && formik.errors[n] ? "is-invalid" : "");

    return (
        <>
            {/* ---- Indicateurs ---- */}
            <div className="row">
                <Kpi label="Produits (CA net HT)" value={formatAmount(fin.produitsNets)}
                    sub={fin.retoursClients ? `dont retours : −${formatAmount(fin.retoursClients)}` : null} />
                <Kpi label="Charges réalisées HT" value={formatAmount(fin.chargesTotales)}
                    sub={`Factures ${formatAmount(fin.achatsFactures - fin.retoursFournisseurs)} · Dépenses ${formatAmount(fin.depensesDirectes)} · Main-d'œuvre ${formatAmount(fin.mainOeuvreTaches)}`} />
                <Kpi label="Engagé (BC non facturés)" value={formatAmount(fin.engage)} />
                <Kpi label="Marge" value={formatAmount(fin.marge)}
                    sub={fin.tauxMarge != null ? `${fin.tauxMarge} % du CA` : "Pas encore de CA"}
                    tone={fin.marge < 0 ? "neg" : fin.marge > 0 ? "pos" : ""} />
            </div>

            <div className="row">
                {/* ---- Budget ---- */}
                <div className="col-lg-6 mb-3">
                    <div className="card h-100">
                        <div className="card-header py-2"><strong>Budget</strong></div>
                        <div className="card-body">
                            {fin.budget == null ? (
                                <p className="text-muted mb-0">Aucun budget défini : renseignez-le via « Modifier » sur le projet.</p>
                            ) : (
                                <>
                                    <div className="d-flex justify-content-between small mb-1">
                                        <span>Consommé + engagé : <strong>{formatAmount(fin.chargesTotales + fin.engage)}</strong></span>
                                        <span>Budget : <strong>{formatAmount(fin.budget)}</strong></span>
                                    </div>
                                    <div className="progress gp-budget-bar">
                                        <div className={`progress-bar ${consoTone}`} style={{ width: `${Math.min(100, conso ?? 0)}%` }}>{conso ?? 0} %</div>
                                    </div>
                                    <div className={`mt-3 h5 ${fin.resteBudget < 0 ? "text-danger" : ""}`}>
                                        {fin.resteBudget < 0
                                            ? <><i className="fas fa-exclamation-triangle mr-1"></i>Dépassement : {formatAmount(-fin.resteBudget)}</>
                                            : <>Reste disponible : {formatAmount(fin.resteBudget)}</>}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ---- Répartition des charges ---- */}
                <div className="col-lg-6 mb-3">
                    <div className="card h-100">
                        <div className="card-header py-2"><strong>Répartition des charges</strong></div>
                        <div className="card-body">
                            {fin.parCategorie.length === 0 && <p className="text-muted mb-0">Aucune charge pour l'instant.</p>}
                            {fin.parCategorie.map((c) => (
                                <div key={c.categorie} className="mb-2">
                                    <div className="d-flex justify-content-between small"><span>{c.categorie}</span><strong>{formatAmount(c.montant)}</strong></div>
                                    <div className="progress gp-bar"><div className="progress-bar" style={{ width: `${(c.montant / totalCharges) * 100}%` }}></div></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Dépenses directes ---- */}
            <div className="card">
                <div className="card-header py-2 d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Dépenses directes</strong>
                        <small className="text-muted ml-2">main-d'œuvre, frais divers… (les factures d'achat sont comptées automatiquement)</small>
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={openCreate}><i className="fas fa-plus mr-1"></i>Ajouter</button>
                </div>
                <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                        <thead className="thead-light">
                            <tr><th>Date</th><th>Catégorie</th><th>Libellé</th><th>Fournisseur</th><th>Réf.</th><th className="text-right">Montant HT</th><th></th></tr>
                        </thead>
                        <tbody>
                            {depenses.length === 0 && <tr><td colSpan="7" className="text-center text-muted py-3">Aucune dépense</td></tr>}
                            {depenses.map((d) => {
                                const c = findCategorieDepense(d.categorie);
                                return (
                                    <tr key={d.id}>
                                        <td className="small text-nowrap">{formatDate(d.date)}</td>
                                        <td><i className={`fas ${c.icon} text-muted mr-1`}></i>{c.label}</td>
                                        <td>{d.libelle}</td>
                                        <td>{d.supplierName || "—"}</td>
                                        <td className="small">{d.reference || "—"}</td>
                                        <td className="text-right text-nowrap">{formatAmount(d.montant)}</td>
                                        <td className="text-right text-nowrap">
                                            <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => openEdit(d)}><i className="fas fa-edit"></i></button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => remove(d)}><i className="fas fa-trash"></i></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {depenses.length > 0 && (
                            <tfoot>
                                <tr><th colSpan="5" className="text-right">Total</th><th className="text-right">{formatAmount(fin.depensesDirectes)}</th><th></th></tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            {/* ---- Modal dépense ---- */}
            <div className="modal fade" id={DEPENSE_MODAL}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5><i className="fas fa-receipt mr-2"></i>{editing ? "Modifier la dépense" : "Nouvelle dépense"}</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group col-md-6">
                                    <label>Date</label>
                                    <input type="date" name="date" className={`form-control ${invalid("date")}`} value={formik.values.date} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    <div className="invalid-feedback">{formik.errors.date}</div>
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Catégorie</label>
                                    <select name="categorie" className="form-control" value={formik.values.categorie} onChange={formik.handleChange}>
                                        {CATEGORIES_DEPENSE.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Libellé *</label>
                                <input name="libelle" className={`form-control ${invalid("libelle")}`} value={formik.values.libelle} onChange={formik.handleChange} onBlur={formik.handleBlur}
                                    placeholder="ex : Équipe maçonnerie semaine 42" />
                                <div className="invalid-feedback">{formik.errors.libelle}</div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-6">
                                    <label>Montant HT (DH) *</label>
                                    <input type="number" step="0.01" min="0" name="montant" className={`form-control ${invalid("montant")}`} value={formik.values.montant} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    <div className="invalid-feedback">{formik.errors.montant}</div>
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Référence</label>
                                    <input name="reference" className="form-control" value={formik.values.reference} onChange={formik.handleChange} placeholder="n° de reçu, ticket…" />
                                </div>
                            </div>
                            <div className="form-group mb-0">
                                <label>Fournisseur (facultatif)</label>
                                <select name="supplierId" className="form-control" value={formik.values.supplierId} onChange={formik.handleChange}>
                                    <option value="">— Aucun —</option>
                                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProjetFinances;
